// frontend/src/components/Settings/AdminNotificationSettings.jsx

import React, { useState, useEffect } from 'react';
import { notificationsAPI, usersAPI } from '../../api/client';
import { useTranslation } from '../../context/TranslationContext';
import { useToast } from '../../hooks/useToast';
import Toast from '../Common/Toast';
import SearchableSelect from '../Common/SearchableSelect';
import { SubscriptionList } from './NotificationSettings';
import './NotificationSettings.css';

// Admin-scoped API wrapper — all calls use /admin/* routes (company_id check only)
const makeAdminAPI = (targetUserId) => ({
  // If subscription_id is null (no row yet), upsert first then treat as update
  updateSubscription: async (id, data) => {
    if (!id) throw new Error('No subscription_id — use ensureSubscription first');
    return notificationsAPI.adminUpdateSubscriptionFn(id, data);
  },
  ensureSubscription: (data) => notificationsAPI.adminEnsureSubscription(targetUserId, data),
  addFilter:          (subId, data)    => notificationsAPI.addAdminFilter(subId, data),
  updateFilter:       (filterId, data) => notificationsAPI.adminUpdateFilter(filterId, data),
  deleteFilter:       (filterId)       => notificationsAPI.adminDeleteFilter(filterId),
});

const AdminNotificationSettings = ({ user, onClose }) => {
  const { t } = useTranslation();
  const { toasts, removeToast, toast_success, toast_error } = useToast();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [copyFromUserId, setCopyFromUserId] = useState('');
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    load();
    usersAPI.getAll()
      .then(r => { if (r.data.success) setAllUsers(r.data.users || []); })
      .catch(() => {});
  }, [user.user_id]);


  useEffect(() => {
    console.log('[AdminNotificationSettings.jsx] mounted');
    return () => console.log('[AdminNotificationSettings.jsx] unmounted');
  }, []);
  

  const load = async () => {
    try {
      setLoading(true);
      const r = await notificationsAPI.adminGetUserSubscriptions(user.user_id);
      if (r.data.success) setSubscriptions(r.data.data);
    } catch {
      toast_error(t('messages.error_load', 'Failed to load'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!copyFromUserId) return;
    const fromUser = allUsers.find(u => u.user_id === parseInt(copyFromUserId));
    if (!window.confirm(
      `${t('notifications.copy_confirm', 'This will replace all notification settings for')} ${user.full_name || user.username}. ${t('continue', 'Continue?')}`
    )) return;
    try {
      setCopying(true);
      await notificationsAPI.copySubscriptions(copyFromUserId, user.user_id);
      toast_success(t('notifications.copied_success', 'Settings copied successfully'));
      setCopyFromUserId('');
      await load();
    } catch {
      toast_error('Copy failed');
    } finally {
      setCopying(false);
    }
  };

  // User options for SearchableSelect
  const userOptions = allUsers
    .filter(u => u.user_id !== user.user_id)
    .map(u => ({
      value: u.user_id,
      code: u.username,
      name: u.full_name || u.username,
    }));

  return (
    // No onClick on overlay — modal stays open when clicking outside
    <div className="modal-overlay">
      <div className="admin-notif-modal">
        {/* Header */}
        <div className="admin-notif-header">
          <div className="admin-notif-title">
            <span>{t('notifications.admin_title', 'Notification Settings')}</span>
            <span className="for-user">
              {t('notifications.for_user', 'for')} <strong>{user.full_name || user.username}</strong>
            </span>
          </div>

          <div className="admin-notif-actions">
            {/* Copy from user — searchable */}
            <div className="copy-from-wrap">
              <div className="copy-from-select-wrap">
                <SearchableSelect
                  value={copyFromUserId}
                  onChange={val => setCopyFromUserId(val)}
                  options={userOptions}
                  getOptionLabel={opt => opt.name ? `${opt.code} — ${opt.name}` : opt.code}
                  getOptionValue={opt => opt.value}
                  formatDisplay={opt => opt.name ? `${opt.code} — ${opt.name}` : opt.code}
                  placeholder={t('notifications.copy_from', 'Copy from user…')}
                />
              </div>
              <button
                className="btn-copy"
                onClick={handleCopy}
                disabled={!copyFromUserId || copying}
              >
                {copying ? '⏳' : '⎘'} {t('notifications.copy_from', 'Copy')}
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="admin-notif-body">
          {loading ? (
            <div className="loading">Loading…</div>
          ) : (
            <div className="nsub-list">
              <SubscriptionList
                subscriptions={subscriptions}
                setSubscriptions={setSubscriptions}
                apiModule={makeAdminAPI(user.user_id)}
                toast_success={toast_success}
                toast_error={toast_error}
              />
            </div>
          )}
        </div>

        {/* Footer close */}
        <div className="admin-notif-footer">
          <button className="btn-close-modal" onClick={onClose}>
            {t('close', 'Close')}
          </button>
        </div>

        <div className="toast-container">
          {toasts.map(toast => <Toast key={toast.id} {...toast} onClose={removeToast} />)}
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationSettings;